import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash2, Loader2 } from "lucide-react";
import { useEstimateCalculations } from "@/hooks/useEstimateCalculations";
import { useEstimateLineItems } from "@/hooks/useEstimateLineItems";

interface LineItemsTableProps {
  estimateId: string;
}

const getCalculationBadge = (source?: 'auto' | 'manual' | 'edited') => {
  if (source === 'auto') {
    return <Badge variant="default" className="text-xs ml-1">AUTO</Badge>;
  } else if (source === 'edited') {
    return <Badge variant="secondary" className="text-xs ml-1">EDITED</Badge>;
  } else if (source === 'manual') {
    return <Badge variant="outline" className="text-xs ml-1">MANUAL</Badge>;
  }
  return null;
};

const LineItemsTable = ({ estimateId }: LineItemsTableProps) => {
  const { lineItems, isLoading, updateLineItem, deleteLineItem } = useEstimateLineItems(estimateId);
  const { calculateLineTotal } = useEstimateCalculations(lineItems);

  const handleUpdateQuantity = (item: any, quantity: number) => {
    const { lineTotal, discountAmount } = calculateLineTotal(
      quantity,
      item.unit_price,
      item.discount_percent
    );

    updateLineItem.mutate({
      id: item.id,
      updates: {
        quantity,
        discount_amount: discountAmount,
        line_total: lineTotal,
        calculation_source: item.calculation_source === 'auto' ? 'edited' : item.calculation_source,
      },
    });
  };

  const handleUpdatePrice = (item: any, unitPrice: number) => {
    const { lineTotal, discountAmount } = calculateLineTotal(
      item.quantity,
      unitPrice,
      item.discount_percent
    );

    updateLineItem.mutate({
      id: item.id,
      updates: {
        unit_price: unitPrice,
        discount_amount: discountAmount,
        line_total: lineTotal,
      },
    });
  };

  const handleUpdateDiscount = (item: any, discountPercent: number) => {
    const { lineTotal, discountAmount } = calculateLineTotal(
      item.quantity,
      item.unit_price,
      discountPercent
    );

    updateLineItem.mutate({
      id: item.id,
      updates: {
        discount_percent: discountPercent,
        discount_amount: discountAmount,
        line_total: lineTotal,
      },
    });
  };

  const handleRemove = (id: string) => {
    deleteLineItem.mutate(id);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Line Items</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <TableHead className="w-24">Quantity</TableHead>
              <TableHead className="w-20">Unit</TableHead>
              <TableHead className="w-32">Unit Price</TableHead>
              <TableHead className="w-24">Discount %</TableHead>
              <TableHead className="w-32 text-right">Line Total</TableHead>
              <TableHead className="w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {lineItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground">
                  No items added yet. Add products from the Products tab.
                </TableCell>
              </TableRow>
            ) : (
              lineItems.map((item) => (
                 <TableRow key={item.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {item.description}
                      {getCalculationBadge(item.calculation_source)}
                    </div>
                    {item.calculation_note && (
                      <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
                        {item.calculation_note}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) =>
                        handleUpdateQuantity(item, parseFloat(e.target.value) || 0)
                      }
                      className="w-24"
                    />
                  </TableCell>
                  <TableCell>{item.unit}</TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unit_price}
                      onChange={(e) =>
                        handleUpdatePrice(item, parseFloat(e.target.value) || 0)
                      }
                      className="w-32"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={item.discount_percent || 0}
                      onChange={(e) =>
                        handleUpdateDiscount(item, parseFloat(e.target.value) || 0)
                      }
                      className="w-24"
                    />
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    ${item.line_total.toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemove(item.id)}
                      disabled={deleteLineItem.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default LineItemsTable;
